import {
  API_ROUTES,
  type Customer,
  type CustomerListResponse,
  type CustomerWriteRequest,
} from '@oscom/shared';
import { apiGet, isApiErrorResponse } from './client';

/**
 * Customer API bindings.
 *
 * `listCustomers` and `getCustomer` reuse the shared `apiGet` helper (typed
 * errors, uniform network handling). `saveCustomer` needs its own fetch call
 * because a write also has to succeed with a body (`response.json()` on 200),
 * but its error handling is otherwise the same as `apiGet`: the API builds a
 * specific, user-safe German message per status (see `errors.ts` on the API
 * side, including the 409 message for a concurrent edit) and `saveCustomer`
 * reads and surfaces it unchanged.
 */

export function listCustomers(): Promise<CustomerListResponse> {
  return apiGet<CustomerListResponse>(API_ROUTES.customers);
}

export function getCustomer(id: string): Promise<Customer> {
  return apiGet<Customer>(`${API_ROUTES.customers}/${encodeURIComponent(id)}`);
}

export async function saveCustomer(id: string, write: CustomerWriteRequest): Promise<Customer> {
  let response: Response;
  try {
    response = await fetch(`${API_ROUTES.customers}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(write),
    });
  } catch {
    throw new Error('Der Dienst ist nicht erreichbar.');
  }

  if (!response.ok) {
    const rawBody: unknown = await response
      .clone()
      .json()
      .catch(() => undefined);
    if (isApiErrorResponse(rawBody)) {
      throw new Error(rawBody.error.message);
    }
    throw new Error('Speichern fehlgeschlagen (HTTP ' + response.status + ')');
  }
  return (await response.json()) as Customer;
}
